import React from 'react';
import LivePage from './LivePage';
import submenuTests from './menus/submenu-tests';
import { ImagePreview } from '../components/images/ImagePreview';
import BadgeColorHash from '../components/common/BadgeColorHash';
import SimilarityBadge from '../components/common/SimilarityBadge';
import BadgeId from '../components/common/BadgeId';
import FuseboxClusteringBenchmarkResult from '../components/fusebox-benchmark/FuseboxClusteringBenchmarkResult';
import {
  clusterPairAdminUrl,
  toOriginalImageUrl,
  FAIL_TYPE_HELP,
} from '../components/fusebox-benchmark/fuseboxBenchmarkUtils';
import LegoAdminPageContext from './legoAdminPageContext';

export default class FuseboxEmbeddingBenchmarkPage extends LivePage {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      samples: [],
      savedRuns: [],
      setups: [],
      setup: this.getUrlParam('setup') || '',
      sampleName: this.getUrlParam('sample') || '',
      // Shared + per-test params (kept as strings for the inputs).
      n: String(this.getUrlParam('n') || '200'),
      threshold: String(this.getUrlParam('threshold') || '0.9'), // unit-scale [0,1] similarity (0.9 == cosine 0.8)
      superclusterThreshold: String(this.getUrlParam('superclusterThreshold') || '0.95'),
      seed: String(this.getUrlParam('seed') || '42'),
      negScope: this.getUrlParam('negScope') || 'same_model',
      date: this.getUrlParam('date') || new Date(Date.now() - 180 * 24 * 3600 * 1000).toISOString().slice(0, 10),
      order: this.getUrlParam('order') || 'hash',
      pairResult: null,
      clusterResult: null,
      running: '',
      error: '',
      saveLabel: '',
    };
    this.submenu = submenuTests;
    this.benchSvc = this.service('services/fusebox-embedding-benchmark');
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadIndex();
  }

  async loadIndex() {
    try {
      const res = await this.benchSvc.find({ query: {} });
      this.setState({
        samples: res.samples || [],
        savedRuns: res.savedRuns || [],
        setups: res.setups || [],
        sampleName: this.state.sampleName || (res.samples || []).find((s) => s.hasModeration)?.name || '',
      });
    } catch (err) {
      this.setState({ error: String(err.message || err) });
    }
  }

  async runTest(test) {
    const { sampleName, n, threshold, superclusterThreshold, seed, negScope, date, setup, order } = this.state;
    // The pair test needs a moderated sample as ground truth; the clustering simulation runs over
    // the whole DB collection and ignores the sample, so it can run with none selected.
    if (test === 'pairs' && !sampleName) return;
    this.setUrlParams({ sample: sampleName, setup, n, threshold, superclusterThreshold, seed, negScope, date, order });
    this.setState({ running: test, error: '' });
    try {
      const query = { test, name: sampleName, n, threshold, seed, setup };
      if (test === 'pairs') query.negScope = negScope;
      if (test === 'clustering') {
        query.date = date;
        query.order = order;
        query.superclusterThreshold = superclusterThreshold;
      }
      const result = await this.benchSvc.find({ query });
      this.setState({
        [test === 'pairs' ? 'pairResult' : 'clusterResult']: result,
        running: '',
      });
    } catch (err) {
      this.setState({ error: String(err.message || err), running: '' });
    }
  }

  async removeRun(run) {
    try {
      await this.benchSvc.remove(encodeURIComponent(run.saved_at));
      await this.loadIndex();
    } catch (err) {
      this.setState({ error: String(err.message || err) });
    }
  }

  async saveRun(result) {
    const label = this.state.saveLabel.trim();
    if (!label || !result) return;
    try {
      await this.benchSvc.create({ label, result });
      this.setState({ saveLabel: '' });
      await this.loadIndex();
    } catch (err) {
      this.setState({ error: String(err.message || err) });
    }
  }

  numInput(key, label, step = 1, title = undefined) {
    return (
      <label className="mr-3 mb-0 small" title={title}>
        {label}{' '}
        <input
          type="number"
          step={step}
          className="form-control form-control-sm d-inline-block"
          style={{ width: 90 }}
          value={this.state[key]}
          onChange={(e) => this.setState({ [key]: e.target.value })}
        />
      </label>
    );
  }

  renderControls() {
    const { samples, sampleName, setups, setup, negScope, date, order, running } = this.state;
    return (
      <div className="border rounded bg-light p-2 mb-3">
        <div className="d-flex flex-wrap align-items-center mb-2">
          <label className="mr-3 mb-0 small">
            Sample{' '}
            <select
              className="form-control form-control-sm d-inline-block"
              style={{ width: 220 }}
              value={sampleName}
              onChange={(e) => this.setState({ sampleName: e.target.value })}
            >
              <option value="">(whole collection — clustering only)</option>
              {samples.map((s) => (
                <option key={s.name} value={s.name} disabled={!s.hasModeration}>
                  {s.name}{s.hasModeration ? '' : ' (no moderation)'}
                </option>
              ))}
            </select>
          </label>
          <label className="mr-3 mb-0 small">
            Embedding setup{' '}
            <select
              className="form-control form-control-sm d-inline-block"
              style={{ width: 300 }}
              value={setup}
              onChange={(e) => this.setState({ setup: e.target.value })}
            >
              <option value="">(default setup)</option>
              {setups.map((s) => (
                <option key={s.setupName} value={s.setupName}>
                  {s.setupName || '(unnamed)'} — {s.count} imgs
                </option>
              ))}
            </select>
          </label>
          {this.numInput('n', 'N')}
          {this.numInput('threshold', 'Threshold', 0.01)}
          {this.numInput('seed', 'Seed')}
        </div>
        <div className="d-flex flex-wrap align-items-center">
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            disabled={!!running || !sampleName}
            title={!sampleName ? 'Select a moderated sample to run the pair test' : undefined}
            onClick={() => this.runTest('pairs')}
          >
            {running === 'pairs' ? 'Running…' : 'Run pair test'}
          </button>
          <label className="mr-4 mb-0 small">
            negatives{' '}
            <select
              className="form-control form-control-sm d-inline-block"
              style={{ width: 130 }}
              value={negScope}
              onChange={(e) => this.setState({ negScope: e.target.value })}
            >
              <option value="same_model">same model</option>
              <option value="any">any cluster</option>
            </select>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            disabled={!!running}
            onClick={() => this.runAsync(this.runTest('clustering'), 'Running clustering simulation…')}
          >
            {running === 'clustering' ? 'Running…' : 'Run clustering simulation'}
          </button>
          {this.numInput('superclusterThreshold', 'Supercluster thr', 0.01,
            'Prefer the supercluster (linked clusters) pick when the best same-model cluster scores below this [0,1] similarity')}
          <label className="mr-3 mb-0 small">
            cutoff date{' '}
            <input
              type="date"
              className="form-control form-control-sm d-inline-block"
              style={{ width: 160 }}
              value={date}
              onChange={(e) => this.setState({ date: e.target.value })}
            />
          </label>
          <label
            className="mb-0 small"
            title="hash: seeded sample, comparable across embedding setups · date: the first N images uploaded after the cutoff"
          >
            images{' '}
            <select
              className="form-control form-control-sm d-inline-block"
              style={{ width: 180 }}
              value={order}
              onChange={(e) => this.setState({ order: e.target.value })}
            >
              <option value="hash">seeded sample (hash)</option>
              <option value="date">first N after cutoff</option>
            </select>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ml-auto"
            disabled={!this.state.pairResult && !this.state.clusterResult}
            onClick={() => this.setState({ pairResult: null, clusterResult: null })}
          >
            Clear results
          </button>
        </div>
      </div>
    );
  }

  renderSummaryTable(summary, fields) {
    return (
      <table className="table table-sm w-auto mb-2">
        <tbody>
          {fields.map(([key, label, isPct]) => {
            const v = summary[key];
            const text = v == null
              ? '—'
              : isPct
                ? `${(v * 100).toFixed(1)}%`
                : typeof v === 'object'
                  ? JSON.stringify(v)
                  : String(v);
            return (
              <tr key={key}>
                <th className="small text-muted pr-3">{label}</th>
                <td className="small">{text}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  renderSweep(sweep, bestThreshold) {
    if (!sweep?.length) return null;
    const max = Math.max(...sweep.map((s) => s.accuracy));
    return (
      <div className="mb-2" style={{ maxWidth: 460 }}>
        <div className="small text-muted">accuracy by threshold (best {bestThreshold})</div>
        {sweep.filter((s, i) => i % 2 === 0).map((s) => (
          <div key={s.threshold} className="d-flex align-items-center" style={{ lineHeight: '14px' }}>
            <span className="small text-muted mr-1" style={{ width: 42 }}>
              {s.threshold.toFixed(2)}
            </span>
            <div
              style={{
                width: `${Math.round((s.accuracy / max) * 360)}px`,
                height: 9,
                background: s.threshold === bestThreshold ? '#2563eb' : '#9ec0ee',
              }}
            />
            <span className="small ml-1">{(s.accuracy * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    );
  }

  renderRunHeader(result) {
    return (
      <div className="small text-muted mb-2">
        embedding <strong>{result.embedding?.setupName || '?'}</strong>
        {' '}(dim {result.embedding?.dim}, {result.embedding?.total} vectors) ·
        sample {result.params?.name} · seed {result.params?.seed} ·
        threshold {result.params?.threshold} · {result.generated_at}
      </div>
    );
  }

  renderSaveControls(result) {
    return (
      <div className="d-flex align-items-center mb-3">
        <input
          type="text"
          placeholder="run label, e.g. head_v2"
          className="form-control form-control-sm mr-2"
          style={{ width: 220 }}
          value={this.state.saveLabel}
          onChange={(e) => this.setState({ saveLabel: e.target.value })}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-success"
          disabled={!this.state.saveLabel.trim()}
          onClick={() => this.saveRun(result)}
        >
          Save run for comparison
        </button>
      </div>
    );
  }

  renderPairResult() {
    const result = this.state.pairResult;
    if (!result) return null;
    const s = result.summary;
    return (
      <div className="border rounded p-3 mb-4">
        <h5>Pair test — accuracy {(s.accuracy * 100).toFixed(1)}%</h5>
        {this.renderRunHeader(result)}
        <div className="d-flex flex-wrap">
          {this.renderSummaryTable(s, [
            ['pairs_tested', 'pairs tested'],
            ['positives_within_cluster', 'positives within cluster'],
            ['positives_linked_clusters', 'positives across linked clusters'],
            ['accuracy', 'accuracy @ threshold', true],
            ['precision_same', 'precision (same)', true],
            ['recall_same', 'recall (same)', true],
            ['auc', 'AUC'],
            ['best_threshold', 'best threshold'],
            ['best_threshold_accuracy', 'accuracy @ best', true],
            ['confusion', 'confusion (tp/fp/tn/fn)'],
          ])}
          <div className="ml-4">{this.renderSweep(result.sweep, s.best_threshold)}</div>
        </div>
        {this.renderSaveControls(result)}
        <h6>Failures ({result.failures.length} shown)</h6>
        {result.failures.map((f, i) => (
          <div key={i} className="d-flex align-items-center border-bottom py-1">
            <span
              className={`badge mr-2 ${f.fail_type === 'false_same' ? 'badge-danger' : 'badge-warning'}`}
              title={FAIL_TYPE_HELP[f.fail_type]}
            >
              {f.fail_type}
            </span>
            {f.pair_scope === 'linked_clusters' ? (
              <span
                className="badge badge-info mr-2"
                title="positive pair spanning two linked clusters of the same template group"
              >
                linked
              </span>
            ) : null}
            <SimilarityBadge value={f.similarity} digits={4} title="pair similarity [0,1]" />
            {[f.image_a, f.image_b].map((img, k) => (
              <div key={k} className="text-center mx-2">
                <ImagePreview
                  url={toOriginalImageUrl(img.image_url)}
                  img200
                  contain
                  zoom
                  title={img.image_url}
                />
                <div className="small"><BadgeId id={img.image_id} /></div>
                <div className="small text-muted">
                  {img.model_id} <BadgeColorHash id={img.cluster_id} />
                </div>
              </div>
            ))}
            <a
              className="btn btn-sm btn-outline-secondary ml-2 py-0"
              target="_blank"
              rel="noreferrer"
              href={clusterPairAdminUrl(f.image_a.cluster_id, f.image_b.cluster_id)}
            >
              ⧉ clusters
            </a>
          </div>
        ))}
      </div>
    );
  }

  renderClusterResult() {
    const { clusterResult, saveLabel } = this.state;
    return (
      <FuseboxClusteringBenchmarkResult
        result={clusterResult}
        saveLabel={saveLabel}
        onSaveLabelChange={(v) => this.setState({ saveLabel: v })}
        onSave={(result) => this.saveRun(result)}
      />
    );
  }

  renderSavedRuns() {
    const { savedRuns } = this.state;
    if (!savedRuns.length) return null;
    return (
      <div className="border rounded p-3 mb-4">
        <h6>
          Saved runs (compare embeddings on identical params: same sample, N, seed, threshold)
        </h6>
        <table className="table table-sm small">
          <thead>
            <tr>
              <th>label</th>
              <th>test</th>
              <th>embedding</th>
              <th>sample</th>
              <th>N</th>
              <th>seed</th>
              <th>thr</th>
              <th>accuracy</th>
              <th>full / AUC</th>
              <th>best thr</th>
              <th>saved</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {savedRuns.map((r, i) => (
              <tr key={i}>
                <td><strong>{r.label}</strong></td>
                <td>{r.test}</td>
                <td>{r.embedding?.setupName}</td>
                <td>{r.params?.name}</td>
                <td>{r.params?.n}</td>
                <td>{r.params?.seed}</td>
                <td>{r.params?.threshold}</td>
                <td>
                  {r.test === 'pairs'
                    ? `${((r.summary?.accuracy || 0) * 100).toFixed(1)}%`
                    : `${((r.summary?.decision_accuracy || 0) * 100).toFixed(1)}%`}
                </td>
                <td>
                  {r.test === 'pairs'
                    ? (r.summary?.auc ?? '—')
                    : `${((r.summary?.full_accuracy || 0) * 100).toFixed(1)}%`}
                </td>
                <td>
                  {r.summary?.best_threshold}{' '}
                  ({((r.summary?.best_threshold_accuracy || 0) * 100).toFixed(1)}%)
                </td>
                <td className="text-muted">
                  {(r.saved_at || '').slice(0, 16).replace('T', ' ')}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger py-0"
                    title="Delete this saved run"
                    onClick={() => this.removeRun(r)}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderPageBody() {
    const { error } = this.state;
    return (
      <div className="container-fluid mt-2">
        <h4>Fusebox embedding benchmark</h4>
        <div className="small text-muted mb-2">
          Pair test: N image pairs, half same-cluster / half different-cluster
          (linked clusters never count as different) — predict same by similarity ≥ threshold.
          All similarities and thresholds are on the [0,1] unit scale (0.5 = orthogonal, 1 = identical).
          Clustering simulation: rebuild clusters as of the cutoff date, then for N later images
          decide join-vs-new and compare with what actually happened.
        </div>
        {error ? <div className="alert alert-danger">{error}</div> : null}
        {this.renderControls()}
        {this.renderSavedRuns()}
        {this.renderPairResult()}
        {this.renderClusterResult()}
      </div>
    );
  }
}

FuseboxEmbeddingBenchmarkPage.contextType = LegoAdminPageContext;